import { useCallback, useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { FormField, Label, Select } from '@zillow/constellation-10';
import { debounce } from '@zg-rentals/zdash';

import { AppSelectors } from '../../../../selectors';
import { useSelector } from '../../../../selectors/useSelector';

import WizardServerError from '../../../../model/WizardServerError';

import { ModularListingContext } from '../../../../containers/CreateListingPage/context';
import { WizardContext } from '../../../../components/Wizard/context';

import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';
import TimeConstants from '../../../../constants/TimeConstants';
import { leaseDurationOptions } from '../../../../constants/ListingDetailsConstants';

import useForm from '../../../../hooks/useForm';

import { postingConstraints } from '../../../../utils/formValidation';
import PropertiesActions from '../../../../actions/PropertiesActions';
import PostingActions from '../../../../actions/PostingActions';
import actions from '../../../../containers/CreateListingPage/actions';
import wizardActions from '../../../../components/Wizard/actions';

import WizardAlert from '../../../../components/WizardAlert';

import type React from 'react';

const inputName = 'leaseDuration';

const LeaseDurationFieldSet = () => {
  const dispatch = useDispatch();

  const { reducerWizardDispatch } = useContext(WizardContext);
  const {
    modularListingState: { isInModalState, isSaveComplete, serverErrorObj, shouldSaveOnBlur },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const { listingAlias, leaseDuration } = useSelector((state) => state.properties.currentProperty);
  const isScreenDesktop = useSelector((state) => AppSelectors.makeIsScreenDesktop(state));

  const {
    inputs,
    errorObj,
    hasFieldError,
    handleChange: handleFormChange,
  } = useForm(
    {
      leaseDuration,
    },
    postingConstraints,
  );

  useEffect(() => {
    if (isSaveComplete) {
      save({
        target: {
          name: inputName,
          // @ts-expect-error
          value: inputs.leaseDuration,
        },
      });
    }
  }, [isSaveComplete]);

  let leaseDurationOptionsValues = leaseDurationOptions;

  if (leaseDuration !== '') {
    leaseDurationOptionsValues = leaseDurationOptions.slice(1);
  }

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const save = (event: React.ChangeEvent<HTMLInputElement> | React.FocusEvent<HTMLSelectElement>) => {
    const { value, name: fieldName } = event.target;

    // field error check needs to be above must click on cta check
    if (hasFieldError(fieldName, value)) {
      actions.updateHasFieldError({ reducerDispatch, hasFieldError: true });
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });

      return;
    }

    actions.updateHasFieldError({ reducerDispatch, hasFieldError: false });

    // return if user must click on cta
    if (!shouldSaveOnBlur && !isSaveComplete) {
      return;
    }

    const dataToSave = {
      fieldName,
      value,
      propertyId: listingAlias,
    };

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    dispatch(PostingActions.property.edit(dataToSave))
      // @ts-expect-error
      .then(() => {
        dispatch(PropertiesActions.updateCurrentProperty({ [fieldName]: value }));

        // reset save cta
        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      })
      .catch((message: string) => {
        updateServerError(message, fieldName);
      })
      .finally(() => {
        wizardActions.updateIsFetchingData({
          reducerWizardDispatch,
          isFetchingData: false,
        });
      });
  };

  const updateServerError = (message = '', fieldName = '') => {
    const displayAt = isInModalState ? DISPLAY_SERVER_ERROR_AT.FIELD : DISPLAY_SERVER_ERROR_AT.PAGE;

    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  const saveDebounced = useCallback(debounce(save, TimeConstants.SELECTION_DEBOUNCE_TIME_MS), []);
  // @ts-expect-error
  const handleChange = (event) => {
    event.persist();
    clearServerError();
    handleFormChange(event);
    if (!shouldSaveOnBlur) {
      saveDebounced(event);
    }
  };

  return (
    <div data-test="LeaseDurationFieldSet">
      <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.FIELD} />
      <FormField
        label={<Label required={true}>Lease duration</Label>}
        control={
          <Select
            size={isScreenDesktop ? 'lg' : 'md'}
            aria-label="Lease duration"
            name={inputName}
            value={String(inputs.leaseDuration ?? '')}
            onChange={handleChange}
            onBlur={saveDebounced}
            required={true}
            // @ts-expect-error - error prop is not yet typed in v10
            error={!!errorObj.leaseDuration}
            css={{ whiteSpace: 'pre' }}
          >
            {leaseDurationOptionsValues.map((option) => {
              return (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              );
            })}
          </Select>
        }
      />
    </div>
  );
};

export default LeaseDurationFieldSet;
